import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Card,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link } from "react-router-dom";
import WhyTecloqCards from "../components/whytecloqcards";

const tabs = [
  "Generative AI",
  "Analytics strategy",
  "Business insights from data",
  "Model creation and scaling",
  "MLOps​",
];

const tabData = [
  {
    title: "Large Language Models (LLMs) for automation and innovation",
    points: [
      "Automate enterprise processes, such as customer experience, contact centers, report generation, and others, and infrastructure with LLMs​",
      "Scale generative AI applications with LLMOps",
      "Solve business problems in different domains with LLMs using frameworks such as LangChain​",
      "Custom train and fine-tune LLMs for domain-specific needs using Reinforcement Learning with Human Feedback (RLHF)​",
      "Design custom prompts to generate creative and informative text​",
      "Ensure the safety, quality, and structure of LLM responses using GuardRails​",
    ],
    tools: [
      "LeapLogic: Automated cloud migration accelerator​​",
      "Gathr: A cloud-native, zero-code platform for building ML-powered applications​",
      "Choice of data vectorization and other tools​",
      "Best practices for enterprise adoption of GenAI​",
    ],
  },
  {
    title:
      "Assessing enterprise analytics for Generative AI & Machine Learning",
    points: [
      "Assess the current state of the customer's analytics​",
      "Modernize rule-based systems",
      "Create roadmaps for the transition to learning-based decision-making with AI/ML​",
      "Enable ML lifecycle best practices​",
      "Recommendations to upgrade to modern technology stacks on-premise or on the cloud​",
    ],
    tools: [
      "Business alignment on use cases​​",
      "Exploratory data analysis and feature engineering​",
      "Performance metrics and model validation.",
      "Solution: Cloud Cost Optimization",
      "Solution: Schema matching for data integration",
    ],
  },
  {
    title: "Data-powered decision-making for business transformation",
    points: [
      "Data discovery for business value​",
      "Recommend novel use cases based on data",
      "Generate and visualize insights with user-friendly dashboards​",
    ],
    tools: [
      "Python, Power BI, and Tableau​​",
      "Proactive business decisions based on generated insights​",
      "Business alignment on feature store for modeling",
    ],
  },
  {
    title: "Democratizing model creation with Generative AI & ML",
    points: [
      "Data pattern identification for feature selection​",
      "Model and prototype creation with analysis and iterative refinement",
      "Pipelines for model development, training, and validation​",
      "Best practices for model performance tuning",
    ],
    tools: [
      "Gather: Drag-and-drop low-code platform for model creation and scaling",
      "Business alignment, data gathering, and data quality enhancement​",
      "Feature engineering and management for structured or unstructured data",
      "Model development infrastructure for code management and collaboration",
      "Solution: Data & AI Labs",
    ],
  },
  {
    title: "Centralized platform for model management",
    points: [
      "Enable best practices for model deployment and management in production",
      "Detect model drift to address changing data distribution patterns using statistical tests",
      "Maintain model performance for risk management and trigger event-driven retraining​",
      "Provide on-premises and cloud-based solutions for model inference",
      "Support collaborative model and metadata management and metrics tracking",
    ],
    tools: [
      "Engineering blueprints and patterns",
      "CI / CD​",
      "Infrastructure as Code",
      "Solution: Open-source orchestration frameworks",
      "Solution: Model explainability and fairness frameworks",
    ],
  },
];

const TabCard1 = ({ title, points, tools }) => (
  <Card
    sx={{
      boxShadow: "none",
      width: "100%",
      backgroundColor: "#FF6DCF",
      borderRadius: "45px",
      padding: "5%",
    }}
  >
    <Typography
      style={{
        fontFamily: "Orbitron",
        fontWeight: "800",
        fontSize: "28px",
        color: "#000000",
        lineHeight: "120%",
        letterSpacing: "2%",
        display: "flex",
        justifyContent: "flex-start",
      }}
    >
      {title}
    </Typography>
    <ul>
      {points.map((point, index) => (
        <li
          key={index}
          style={{
            fontFamily: "Inter",
            fontSize: "18px",
            lineHeight: "120%",
          }}
        >
          {point}
        </li>
      ))}
    </ul>
    <Typography
      style={{
        fontFamily: "Orbitron",
        fontWeight: "800",
        fontSize: "28px",
        color: "#000000",
        lineHeight: "120%",
        letterSpacing: "2%",
        display: "flex",
        justifyContent: "flex-start",
      }}
    >
      Tools and accelerators:
    </Typography>
    <ul>
      {tools.map((tool, index) => (
        <li
          key={index}
          style={{
            fontFamily: "Inter",
            fontSize: "18px",
            lineHeight: "120%",
          }}
        >
          {tool}
        </li>
      ))}
    </ul>
  </Card>
);




const TabCard = ({ title, points, tools }) => (
  <Card
    sx={{
      boxShadow: "none",
      width: { xs: "90%", md: "100%" }, // Centered width for mobile
      backgroundColor: "#FF6DCF",
      borderRadius: { xs: "20px", md: "45px" },
      padding: { xs: "16px", md: "5%" },
      margin: "0 auto", // Center the card horizontally
      display: "flex",
      flexDirection: "column",
      alignItems: "center", // Center content horizontally
      textAlign: "center", // Center text for mobile
    }}
  >
    <Typography
      sx={{
        fontFamily: "Orbitron",
        fontWeight: 800,
        fontSize: { xs: "18px", md: "28px" },
        color: "#000000",
        lineHeight: "120%",
        letterSpacing: "2%",
        marginBottom: { xs: "12px", md: "24px" },
      }}
    >
      {title}
    </Typography>

    {/* Points List */}
    <ul
      style={{
        padding: 0,
        margin: 0,
        textAlign: "left",
        width: "100%",
        listStylePosition: "outside", // Keep bullets outside
        paddingLeft: "20px", // Add padding to align text correctly
      }}
    >
      {points.map((point, index) => (
        <li
          key={index}
          style={{
            fontFamily: "Inter",
            fontSize: `clamp(12px, 2vw, 16px)`, // Responsive font size
            lineHeight: `clamp(110%, 0vw, 100%)`, // Responsive line height
            marginBottom: `clamp(4px, 1vw, 8px)`, // Responsive margin
          }}
        >
          {point}
        </li>
      ))}
    </ul>

    <Typography
      sx={{
        fontFamily: "Orbitron",
        fontWeight: 800,
        fontSize: { xs: "18px", md: "28px" },
        color: "#000000",
        lineHeight: "120%",
        letterSpacing: "2%",
        marginTop: { xs: "16px", md: "24px" },
        marginBottom: { xs: "8px", md: "16px" },
      }}
    >
      Tools and accelerators:
    </Typography>

    {/* Tools List */}
    <ul
      style={{
        padding: 0,
        margin: 0,
        textAlign: "left",
        width: "100%",
        listStylePosition: "outside", // Keep bullets outside
        paddingLeft: "20px", // Add padding to align text correctly
      }}
    >
      {tools.map((tool, index) => (
        <li
          key={index}
          style={{
            fontFamily: "Inter",
            fontSize: `clamp(12px, 2vw, 16px)`, // Responsive font size
            lineHeight: `clamp(110%, 0vw, 100%)`, // Responsive line height
            marginBottom: `clamp(4px, 1vw, 8px)`, // Responsive margin
          }}
        >
          {tool}
        </li>
      ))}
    </ul>
  </Card>
);

const TabCard5 = ({ title, points, tools }) => (
  <Card
    sx={{
      boxShadow: "none",
      width: { xs: "90%", md: "100%" }, // Centered width for mobile
      backgroundColor: "#FF6DCF",
      borderRadius: { xs: "20px", md: "45px" },
      padding: { xs: "16px", md: "5%" },
      margin: "0 auto", // Center the card horizontally
      display: "flex",
      flexDirection: "column",
      alignItems: "center", // Center content horizontally
      textAlign: "center", // Center text for mobile
    }}
  >
    <Typography
      sx={{
        fontFamily: "Orbitron",
        fontWeight: 800,
        fontSize: { xs: "18px", md: "28px" },
        color: "#000000",
        lineHeight: "120%",
        letterSpacing: "2%",
        marginBottom: { xs: "12px", md: "24px" },
      }}
    >
      {title}
    </Typography>

    {/* Points List */}
    <ul
      style={{
        padding: 0,
        margin: 0,
        textAlign: "left",
        width: "100%",
        listStylePosition: "inside", // Keep bullets aligned for mobile
      }}
    >
      {points.map((point, index) => (
        <li
          key={index}
          style={{
            fontFamily: "Inter",
            fontSize: `clamp(14px, 3vw, 18px)`, // Responsive font size
            lineHeight: `clamp(150%, 2vw, 120%)`, // Responsive line height
            marginBottom: `clamp(4px, 1vw, 8px)`, // Responsive margin
          }}
        >
          {point}
        </li>
      ))}
    </ul>

    <Typography
      sx={{
        fontFamily: "Orbitron",
        fontWeight: 800,
        fontSize: { xs: "18px", md: "28px" },
        color: "#000000",
        lineHeight: "120%",
        letterSpacing: "2%",
        marginTop: { xs: "16px", md: "24px" },
        marginBottom: { xs: "8px", md: "16px" },
      }}
    >
      Tools and accelerators:
    </Typography>

    {/* Tools List */}
    <ul
      style={{
        padding: 0,
        margin: 0,
        textAlign: "left",
        width: "100%",
        listStylePosition: "inside", // Keep bullets aligned for mobile
      }}
    >
      {tools.map((tool, index) => (
        <li
          key={index}
          style={{
            fontFamily: "Inter",
            fontSize: `clamp(14px, 3vw, 18px)`, // Responsive font size
            lineHeight: `clamp(150%, 2vw, 120%)`, // Responsive line height
            marginBottom: `clamp(4px, 1vw, 8px)`, // Responsive margin
          }}
        >
          {tool}
        </li>
      ))}
    </ul>
  </Card>
);


const whydata = [
  {
    src: "/assets/AIML/AI1.png",
    alt: "why1",
    title: "Automated Tasks",
    description:
      "Free your employees from repetitive work like data entry and customer support.",
  },
  {
    src: "/assets/AIML/AI2.png",
    alt: "why1",
    title: "Advanced Analytics",
    description:
      "Extract valuable insights from complex data sets for informed decision-making.",
  },
  {
    src: "/assets/AIML/AI3.png",
    alt: "why1",
    title: "Personalized Experiences",
    description:
      "Leverage AI to personalize customer interactions and predict future needs.",
  },
];

const AIML = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Container
        sx={{
          backgroundImage: 'url("/assets/DataAnalytics/background.webp")',
          backgroundPosition: "center",
          backgroundSize: "cover",
          maxWidth: "100% !important",
          height: { xs: "250px", sm: "300px", md: "400px" }, // Responsive height
          display: "flex",
          overflowX: "hidden",
          margin: "0 !important",
          backgroundColor: "#000000",
          fontFamily: "Orbitron",
          fontWeight: "800",
          fontSize: { xs: "36px", sm: "32px", md: "60px" }, // Adjust font size
          color: "#F005A2",
          lineHeight: { xs: "110%", md: "120%" }, // Adjust line height for readability
          letterSpacing: { xs: "1%", md: "2%" }, // Adjust letter spacing
          paddingBottom: { xs: "4%", md: "2%" }, // Adjust padding for smaller screens
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center", // Ensure text alignment on small screens
        }}
      >
        AI & Machine Learning
      </Container>

      {/* <Container
        sx={{
          display: "flex",
          margin: "8% 3% 0 10%",
          flexDirection: "row",
          padding: "0 !important",
        }}
      >
        <Card
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            width: "70%",
          }}
        >
          <img
            src="/assets/AIML/consulting.webp"
            loading="lazy"
            alt="ourgoal"
            style={{ height: "auto", width: "100%" }}
          ></img>
        </Card>
        <Card
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            width: "50%",
            marginLeft: "2%",
            textAlign: "right",
          }}
        >
          <Typography
            style={{
              fontFamily: "Orbitron",
              fontWeight: "800",
              fontSize: "40px",
              color: "#000000",
              lineHeight: "120%",
              letterSpacing: "2%",
              display: "flex",
              justifyContent: "flex-start",
              paddingBottom: "8%",
            }}
          >
            AI & Machine Learning Consulting
          </Typography>
          <Typography
            style={{
              fontFamily: "Orbitron",
              fontWeight: "800",
              fontSize: "25px",
              color: "#000000",
              lineHeight: "120%",
              letterSpacing: "2%",
              display: "flex",
              justifyContent: "flex-start",
              paddingBottom: "8%",
            }}
          >
            Unleash the Power of AI & Machine Learning with Tecloq.
          </Typography>
          <Typography
            style={{
              fontFamily: "Inter",
              fontWeight: "normal",
              color: "#000000",
              fontSize: "20px",
            }}
          >
            answer. These technologies are evolving at unparalleled speed and
            complexity, leaving business leaders uncertain about the impact of
            their AI implementation.
          </Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "8%",
            }}
          >
            <Link
              to="/clientForm"
              style={{
                display: "flex",
                width: "150.84px",
                height: "52.23px",
                backgroundColor: "#F005A2",
                borderRadius: "45.25px",
                opacity: "100%",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 2% 0 2%",
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <div
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: "18px",
                  letterSpacing: "8%",
                  fontWeight: "700",
                }}
              >
                Contact Us
              </div>
              <div
                className="submitBtn__Arrow"
                style={{
                  width: "32.84px",
                  height: "32.23px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "45.25px",
                  opacity: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "7%",
                }}
              >
                <ArrowForwardIcon></ArrowForwardIcon>
              </div>
            </Link>
          </div>
        </Card>
      </Container> */}

      <Container
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          margin: { md: "8% 3% 0 10%" },
          padding: "0 !important",
          alignItems: "center",
          overflow: "hidden", // Prevent overflow
        }}
      >
        {/* Image Section */}
        <Card
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            width: { xs: "100%", md: "70%" },

            marginBottom: { xs: "5%", md: "0" },
            maxWidth: "100%", // Prevent overflow
          }}
        >
          <img
            src="/assets/AIML/consulting.webp"
            loading="lazy"
            alt="ourgoal"
            style={{
              height: "auto",
              width: "100%",
              borderRadius: "10px", // Slight rounding for better look on mobile
              objectFit: "cover",
            }}
          />
        </Card>

        {/* Content Section */}
        <Card
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            width: { xs: "100%", md: "50%" },
            marginLeft: { xs: "0", md: "2%" },
            textAlign: { xs: "center", md: "right" },
            overflow: "hidden", // Prevent text overflow
          }}
        >
          <Typography
            sx={{
              fontFamily: "Orbitron",
              fontWeight: "800",
              fontSize: { xs: "22px", md: "40px" },
              color: "#000000",
              lineHeight: "120%",
              letterSpacing: "2%",
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              paddingBottom: { xs: "4%", md: "8%" },
              wordBreak: "break-word", // Prevent text overflow
            }}
          >
            AI & Machine Learning Consulting
          </Typography>

          <Typography
            sx={{
              fontFamily: "Orbitron",
              fontWeight: "800",
              fontSize: { xs: "16px", md: "25px" },
              color: "#000000",
              lineHeight: "120%",
              letterSpacing: "2%",
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              paddingBottom: { xs: "4%", md: "8%" },
              wordBreak: "break-word", // Prevent text overflow
            }}
          >
            Unleash the Power of AI & Machine Learning with Tecloq
          </Typography>

          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: "normal",
              color: "#000000",
              fontSize: { xs: "14px", md: "20px" },
              lineHeight: "150%",
              textAlign: { xs: "center", md: "right" },
              paddingX: { xs: "5%", md: "0" },
              wordBreak: "break-word", // Prevent overflow for long text
            }}
          >
            These technologies are evolving at unparalleled speed and
            complexity, leaving business leaders uncertain about the impact of
            their AI implementation.
          </Typography>

          {/* Button Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" }, // Center only on mobile
              marginTop: "8%",
            }}
          >
            <Link
              to="/clientForm"
              style={{
                display: "flex",
                width: "150.84px",
                height: "52.23px",
                backgroundColor: "#F005A2",
                borderRadius: "45.25px",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                color: "inherit",
                padding: "0 2%",
              }}
            >
              <Box
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: "16px",
                  fontWeight: "700",
                  letterSpacing: "1%",
                }}
              >
                Contact Us
              </Box>
              <Box
                sx={{
                  width: "28px",
                  height: "28px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "45.25px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "5%",
                }}
              >
                <ArrowForwardIcon
                  sx={{
                    fontSize: { xs: "20px", md: "24px" },
                  }}
                />
              </Box>
            </Link>
          </Box>
        </Card>
      </Container>

      <Box
        sx={{
          maxWidth: "100% !important",
          height: "auto",
          marginTop: "6%",
          paddingX: { xs: "5%", md: "0" }, // Add padding for mobile
        }}
      >
        <Typography
          sx={{
            fontFamily: "Orbitron",
            fontWeight: "800",
            fontSize: { xs: "24px", md: "40px" }, // Smaller font size on mobile
            color: "#000000",
            lineHeight: "120%",
            letterSpacing: "2%",
            display: "flex",
            justifyContent: "center",
            paddingBottom: "1%",
            textAlign: "center", // Center text alignment
          }}
        >
          Boost Efficiency and Make Data-Driven Decisions
        </Typography>
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: "lighter",
            fontSize: { xs: "14px", md: "20px" }, // Adjust font size for mobile
            color: "#000000",
            display: "flex",
            justifyContent: "center",
            margin: { xs: "2% 5%", md: "1% 15% 2% 15%" }, // Adjust margin for mobile
            textAlign: "center", // Ensure text is centered
          }}
        >
          Tecloq (“we,” “our,” or “us”) is committed to protecting the privacy
          of our users and clients. This Privacy Policy outlines how we collect,
          use, and safeguard your information when you interact with our
          website, services, or platforms. By accessing our services, you agree
          to the practices described in this policy.
        </Typography>
      </Box>

      {/* <WhyTecloqCards data={whydata} /> */}
      {/* <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          "& > :not(style)": {
            m: 1,
            width: 370,
            height: 480,
          },
          gap: "3%",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            borderRadius: "3%",
            textAlign: "center",
            border: "0.5px solid #FFC3EA",
            alignContent: "center",
            boxShadow: "0px -3px 17px rgba(240, 5, 162, 0.52)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <img
            src="/assets/AIML/AI1.png"
            loading="lazy"
            alt="why1"
            style={{
              width: "60%",
              height: "45%",
              padding: "15% 0px 10% 15%",
            }}
          ></img>
          <Typography
            sx={{
              fontFamily: "Orbitron",
              lineHeight: "120%",
              fontSize: "28px",
              fontStyle: "normal",
              fontWeight: "700",
              color: "#F005A2",
              letterSpacing: "2%",
            }}
          >
            Automated Tasks
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontStyle: "normal",
              lineHeight: "auto",
              padding: "10% 10% 3% 10%",
            }}
          >
            Free your employees from repetitive work like data entry and
            customer support.
          </Typography>
        </Paper>
        <Paper
          elevation={3}
          sx={{
            borderRadius: "3%",
            textAlign: "center",
            border: "0.5px solid #FFC3EA",
            alignContent: "center",
            boxShadow: "0px -3px 17px rgba(240, 5, 162, 0.52)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <img
            src="/assets/AIML/AI2.png"
            alt="why1"
            style={{
              width: "60%",
              height: "45%",
              padding: "15% 0px 10% 15%",
            }}
          ></img>
          <Typography
            sx={{
              fontFamily: "Orbitron",
              lineHeight: "120%",
              fontSize: "28px",
              fontStyle: "normal",
              fontWeight: "700",
              color: "#F005A2",
              letterSpacing: "2%",
            }}
          >
            Advanced Analytics
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontStyle: "normal",
              lineHeight: "auto",
              padding: "10% 10% 3% 10%",
            }}
          >
            Extract valuable insights from complex data sets for informed
            decision-making.
          </Typography>
        </Paper>
        <Paper
          elevation={3}
          sx={{
            borderRadius: "3%",
            textAlign: "center",
            border: "0.5px solid #FFC3EA",
            alignContent: "center",
            boxShadow: "0px -3px 17px rgba(240, 5, 162, 0.52)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <img
            src="/assets/AIML/AI3.png"
            alt="why1"
            style={{
              width: "60%",
              height: "45%",
              padding: "15% 0px 1% 15%",
            }}
          ></img>
          <Typography
            sx={{
              fontFamily: "Orbitron",
              lineHeight: "120%",
              fontSize: "28px",
              fontStyle: "normal",
              fontWeight: "700",
              color: "#F005A2",
              letterSpacing: "2%",
            }}
          >
            Personalized
            <br />
            Experiences
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontStyle: "normal",
              lineHeight: "auto",
              padding: "10% 10% 3% 10%",
            }}
          >
            Leverage AI to personalize customer interactions and predict future
            needs.
          </Typography>
        </Paper>
      </Box> */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: { xs: "16px", md: "3%" },
          "& > :not(style)": {
            m: 1,
            width: { xs: "100%", sm: "280px", md: "300px" },
            height: { xs: "auto", md: "450px" },
          },
        }}
      >
        {[
          {
            src: "/assets/AIML/AI1.png",
            alt: "why1",
            title: "Automated Tasks",
            description:
              "Free your employees from repetitive work like data entry and customer support.",
          },
          {
            src: "/assets/AIML/AI2.png",
            alt: "why1",
            title: "Advanced Analytics",
            description:
              "Extract valuable insights from complex data sets for informed decision-making.",
          },
          {
            src: "/assets/AIML/AI3.png",
            alt: "why1",
            title: "Personalized Experiences",
            description:
              "Leverage AI to personalize customer interactions and predict future needs.",
          },
        ].map((item, index) => (
          <Paper
            key={index}
            elevation={3}
            sx={{
              borderRadius: "3%",
              textAlign: "center",
              border: "0.5px solid #FFC3EA",
              alignContent: "center",
              boxShadow: "0px -3px 17px rgba(240, 5, 162, 0.52)",
              display: "flex",
              flexDirection: "column",
              padding: "20px",
            }}
          >
            <Box
              component="img"
              src={item.src}
              alt={item.alt}
              loading="lazy"
              sx={{
                width: "60%",
                aspectRatio: "4/3", // Maintain consistent aspect ratio
                objectFit: "contain", // Prevent cropping or distortion
                padding: "5% 0",
                margin: "0 auto",
              }}
            />
            <Typography
              sx={{
                fontFamily: "Orbitron",
                lineHeight: "120%",
                fontSize: { xs: "20px", md: "28px" },
                fontStyle: "normal",
                fontWeight: "700",
                color: "#F005A2",
                letterSpacing: "1%",
                marginTop: "10px",
              }}
            >
              {item.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontStyle: "normal",
                lineHeight: "140%",
                padding: { xs: "5% 5%", md: "10% 10% 3% 10%" },
                fontSize: { xs: "14px", md: "16px" },
              }}
            >
              {item.description}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          backgroundColor: "#FFEFF9",
          maxWidth: "100%",
          marginTop: "5%",
          padding: { xs: "5%", md: "2%" },
        }}
      >
        <Typography
          sx={{
            fontFamily: "Orbitron",
            fontWeight: "800",
            fontSize: { xs: "24px", md: "40px" },
            color: "#000000",
            lineHeight: "120%",
            letterSpacing: "1%",
            textAlign: "center",
            paddingBottom: "10%",
          }}
        >
          Our Artificial Intelligence Consultancy Services Include
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "24px" },
            justifyContent: "center",
            alignItems: "center",
            paddingX: { md: "10%" },
            marginBottom: { md: "5%" },
          }}
        >
          {/* Image Section */}
          <Box
            component="img"
            src="/assets/AIML/ourAI.png"
            alt="our"
            sx={{
              width: { xs: "80%", sm: "60%", md: "50%" },
              alignContent: "center",
              justifyContent: "center",
              height: { xs: "auto", md: "400px" },
              objectFit: "contain",
              flexBasis: { md: "50%" },
            }}
          />

          {/* Card Section */}
          <Card
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              flexBasis: { md: "50%" },
              width: { xs: "100%", md: "auto" },
              textAlign: { xs: "center" },
            }}
          >
            {[
              {
                title: "Custom AI/ML Solutions:",
                description:
                  "Develop and deploy models tailored to your specific needs (cloud or on-premise).",
              },
              {
                title: "Streamlined Implementation:",
                description:
                  "Ensure efficient project execution with data analysis, prototyping, and CI/CD pipelines.",
              },
              {
                title: "Model Management:",
                description:
                  "Monitor, optimize, and manage your AI models throughout their lifecycle.",
              },
              {
                title: "User-Friendly Interfaces:",
                description:
                  "Design intuitive front-ends for seamless AI integration.",
              },
              {
                title: "Expert Support:",
                description:
                  "Access our team of data scientists and machine learning operations professionals.",
              },
              {
                title: "Training and Enablement:",
                description:
                  "Equip your workforce with the knowledge to leverage AI effectively and ethically.",
              },
            ].map((item, index) => (
              <Box key={index} sx={{ marginBottom: "20px" }}>
                <Typography
                  sx={{
                    fontFamily: "Orbitron",
                    lineHeight: "150%",
                    fontSize: { xs: "18px", md: "25px" },
                    fontWeight: "700",
                    color: "#000000",
                    letterSpacing: "1%",
                    marginBottom: "4px",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: "400",
                    color: "#000000",
                    fontSize: { xs: "14px", md: "20px" },
                    lineHeight: "140%",
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Card>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          margin: "10%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          maxWidth: "100% !important",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Orbitron",
            fontWeight: "800",
            fontSize: { xs: "24px", md: "40px" },
            color: "#000000",
            lineHeight: "120%",
            letterSpacing: "2%",
            display: "flex",
            justifyContent: "center",
            marginBottom: "3%",
            textAlign: "center",
          }}
        >
          Seamless infusion of GenAI into your business
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            height: { xs: "auto", md: "70vh" },
            boxSizing: "border-box",
            gap: { xs: "20px", md: "50px" },
            alignItems: "flex-start",
          }}
        >
          {/* ----------------------------------- */}
          {/* Generic tabs */}

          {/* <Tabs
            orientation="vertical"
            value={value}
            onChange={handleChange}
            sx={{
              overflow: "visible !important",
              "& .MuiTabs-indicator": { display: "none" },
              alignItems: "center !important",
              justifyContent: "center !important",
            }}
          >
            {tabs.map((label, index) => (
              <Tab
                key={index}
                label={label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "40.81px 37.54px",
                  gap: "8.16px",
                  width: "499px",
                  height: "80px",
                  backgroundColor: value === index ? "#F005A2" : "transparent",
                  borderLeft: value === index ? "7.35px solid #000000" : "none",
                  color: value === index ? "#FFFFFF !important" : "#000000",
                  fontSize: "18px",
                  fontWeight: value === index ? "bold" : "normal",
                  textTransform: "none",
                  justifyContent: "flex-start",
                }}
              />
            ))}
          </Tabs> */}

          <Tabs
            orientation={isMobile ? "horizontal" : "vertical"}
            value={value}
            onChange={handleChange}
            variant={isMobile ? "scrollable" : "standard"} // Enables scrolling on mobile
            scrollButtons={isMobile ? "auto" : false} // Shows scroll buttons on mobile if needed
            sx={{
              overflowX: isMobile ? "auto" : "visible",
              "& .MuiTabs-indicator": { display: "none" },
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: isMobile ? "flex-start" : "center",
              width: "100%",
            }}
          >
            {tabs.map((label, index) => (
              <Tab
                key={index}
                label={label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: { xs: "8px 12px", md: "40.81px 37.54px" },
                  gap: { xs: "4px", md: "8.16px" },
                  width: { xs: "auto", md: "499px" }, // Make it auto on mobile for flexibility
                  minWidth: { xs: "80px", md: "auto" }, // Ensure it doesn't collapse too much
                  height: { xs: "40px", md: "80px" },
                  backgroundColor: value === index ? "#F005A2" : "transparent",
                  borderLeft: !isMobile
                    ? value === index
                      ? "4px solid #000000"
                      : "none"
                    : "none",
                  borderBottom: isMobile
                    ? value === index
                      ? "4px solid #000000"
                      : "none"
                    : "none",
                  color: value === index ? "#FFFFFF !important" : "#000000",
                  fontSize: { xs: "10px", md: "18px" },
                  fontWeight: value === index ? "bold" : "normal",
                  textTransform: "none",
                  justifyContent: "center",
                }}
              />
            ))}
          </Tabs>

          {/* <Tabs
          orientation={{ xs: "horizontal", md: "vertical" }}
          value={value}
          onChange={handleChange}
          variant="scrollable"
          sx={{
            overflowX: { xs: "auto", md: "visible" },
            "& .MuiTabs-indicator": { display: "none" },
            alignItems: { xs: "center", md: "flex-start" },
            justifyContent: "center",
            width: { xs: "100%", md: "auto" },
          }}
        >
          {tabs.map((label, index) => (
            <Tab
              key={index}
              label={label}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: { xs: "12px 16px", md: "40.81px 37.54px" },
                gap: "8.16px",
                width: { xs: "auto", md: "499px" },
                height: { xs: "auto", md: "80px" },
                backgroundColor:
                  value === index ? "#F005A2" : "transparent",
                borderLeft: value === index
                  ? { xs: "none", md: "7.35px solid #000000" }
                  : "none",
                color: value === index ? "#FFFFFF" : "#000000",
                fontSize: { xs: "14px", md: "18px" },
                fontWeight: value === index ? "bold" : "normal",
                textTransform: "none",
                justifyContent: "flex-start",
              }}
            />
          ))}
        </Tabs> */}

          <Box sx={{ width: "100%" }}>
            {tabData[value] && (
              <TabCard
                title={tabData[value].title}
                points={tabData[value].points}
                tools={tabData[value].tools}
              />
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
export default AIML;
